import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { BottomNav } from "../components/BottomNav";
import { DetailRow } from "../components/DetailRow";
import { InsufficientInfoPanel } from "../components/InsufficientInfoPanel";
import {
  extractionFromReceipt,
  ReceiptDetailsPanel,
  TransactionDetailsPanel,
} from "../components/MatchComparePanels";
import { SiblingNav } from "../components/SiblingNav";
import { SourceBadge } from "../components/SourceBadge";
import { StatusBadge } from "../components/StatusBadge";
import { confidenceLabel, formatDate, formatDateTime, formatMoney } from "../lib/format";
import { loadReceiptMedia } from "../lib/receiptMedia";
import { useAuth } from "../store/auth";
import type { Receipt, Transaction, TransactionListResponse, User } from "../types";

export function TransactionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useAuth((s) => s.user?.role) === "admin";
  const backTo = isAdmin ? "/admin" : "/";
  const [txn, setTxn] = useState<Transaction | null>(null);
  const [matchedReceipt, setMatchedReceipt] = useState<Receipt | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isPdf, setIsPdf] = useState(false);
  const [siblingIds, setSiblingIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [assignTo, setAssignTo] = useState("");
  const [noReceiptOpen, setNoReceiptOpen] = useState(false);
  const [noReceiptReason, setNoReceiptReason] = useState("");

  async function reload() {
    const t = await api.request<Transaction>(`/transactions/${id}`);
    setTxn(t);
    return t;
  }

  useEffect(() => {
    (async () => {
      try {
        setNoReceiptOpen(false);
        setNoReceiptReason("");
        setMatchedReceipt(null);
        setPreviewUrl(null);
        setFileUrl(null);
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Not found");
      }
    })();
  }, [id]);

  useEffect(() => {
    if (!txn?.matched_receipt_id) {
      setMatchedReceipt(null);
      setPreviewUrl(null);
      setFileUrl(null);
      return;
    }
    const receiptId = txn.matched_receipt_id;
    let cancelled = false;
    let revoke: (() => void) | undefined;

    (async () => {
      try {
        const receipt = await api.request<Receipt>(`/receipts/${receiptId}`);
        if (cancelled) return;
        setMatchedReceipt(receipt);
        const pdf =
          (receipt.content_type || "").toLowerCase().includes("pdf") ||
          receipt.original_filename.toLowerCase().endsWith(".pdf");
        setIsPdf(pdf);

        const media = await loadReceiptMedia(receiptId, pdf);
        if (cancelled) {
          media.revoke();
          return;
        }
        revoke = media.revoke;
        setPreviewUrl(media.previewUrl);
        setFileUrl(media.fileUrl);
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e instanceof Error ? e.message : "Could not load matched receipt");
        }
      }
    })();

    return () => {
      cancelled = true;
      revoke?.();
    };
  }, [txn?.matched_receipt_id]);

  useEffect(() => {
    const fromState = (location.state as { siblingIds?: number[] } | null)?.siblingIds;
    if (fromState && fromState.length > 0) {
      setSiblingIds(fromState);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const status = txn?.status;
        const statuses =
          status === "matched" || status === "excluded"
            ? ["matched", "excluded"]
            : ["insufficient", "unmatched", "suggested"];
        const lists = await Promise.all(
          statuses.map((s) =>
            api.request<TransactionListResponse>(
              `/transactions?status=${s}&limit=200`,
            ),
          ),
        );
        if (cancelled) return;
        const ids = lists.flatMap((l) => l.items.map((t) => t.id));
        const current = Number(id);
        if (current && !ids.includes(current)) ids.unshift(current);
        setSiblingIds(ids);
      } catch {
        if (!cancelled && id) setSiblingIds([Number(id)]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, location.state, txn?.status]);

  function goSibling(nextId: number) {
    navigate(`/transactions/${nextId}`, { state: { siblingIds } });
  }

  useEffect(() => {
    if (!isAdmin) return;
    api
      .request<User[]>("/users")
      .then((list) => setUsers(list.filter((u) => u.is_active)))
      .catch(console.error);
  }, [isAdmin]);

  async function assign() {
    if (!txn) return;
    setBusy(true);
    setError(null);
    try {
      await api.request(`/transactions/${txn.id}/assign`, {
        method: "POST",
        body: JSON.stringify({ user_id: assignTo ? Number(assignTo) : null }),
      });
      setAssignTo("");
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not assign");
    } finally {
      setBusy(false);
    }
  }

  async function unmatch() {
    if (!txn || !confirm("Undo this match? The transaction will become open again.")) {
      return;
    }
    setBusy(true);
    try {
      await api.request(`/transactions/${txn.id}/unmatch`, {
        method: "POST",
        body: JSON.stringify({ notes: "Match undone by user" }),
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not unmatch");
    } finally {
      setBusy(false);
    }
  }

  async function markNoReceipt() {
    if (!txn) return;
    const reason = noReceiptReason.trim();
    if (reason.length < 3) {
      setError("Please enter a reason (at least a few words).");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const idx = siblingIds.indexOf(txn.id);
      const remaining = siblingIds.filter((sid) => sid !== txn.id);
      const goTo = remaining[idx] ?? remaining[idx - 1] ?? null;
      await api.request(`/transactions/${txn.id}/exclude`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      });
      setNoReceiptOpen(false);
      setNoReceiptReason("");
      if (goTo != null) {
        navigate(`/transactions/${goTo}`, { state: { siblingIds: remaining } });
      } else {
        await reload();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update");
    } finally {
      setBusy(false);
    }
  }

  async function reopen() {
    if (!txn) return;
    setBusy(true);
    setError(null);
    try {
      await api.request(`/transactions/${txn.id}/reopen`, { method: "POST" });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reopen");
    } finally {
      setBusy(false);
    }
  }

  if (error && !txn) {
    return (
      <div className="app-shell">
        <div className="alert">{error}</div>
        <BottomNav />
      </div>
    );
  }

  if (!txn) {
    return (
      <div className="app-shell">
        <p className="muted">Loading…</p>
        <BottomNav />
      </div>
    );
  }

  const confidence =
    txn.match_confidence != null && txn.match_confidence !== ""
      ? Number(txn.match_confidence)
      : null;
  const hasFx =
    txn.original_amount != null &&
    txn.original_currency != null &&
    (txn.original_currency !== txn.currency ||
      txn.original_amount !== txn.amount);
  const hasReceipt = txn.status === "matched" || txn.status === "insufficient";
  const showSplit = hasReceipt && !!txn.matched_receipt_id;

  return (
    <div className={`app-shell${showSplit ? " match-review" : ""}`}>
      <header className="topbar">
        <Link to={backTo} className="muted">
          ← Back
        </Link>
        <div className="row" style={{ gap: "0.35rem" }}>
          <SourceBadge source={txn.import_source} />
          <StatusBadge status={txn.status} />
        </div>
      </header>

      <SiblingNav
        currentId={txn.id}
        siblingIds={siblingIds}
        onGo={goSibling}
        busy={busy}
      />

      {showSplit && matchedReceipt ? (
        <>
          <div className="match-compare" style={{ marginBottom: "0.85rem" }}>
            <TransactionDetailsPanel txn={txn} confidence={confidence} />
            <ReceiptDetailsPanel
              receipt={matchedReceipt}
              extraction={extractionFromReceipt(matchedReceipt)}
              previewUrl={previewUrl}
              fileUrl={fileUrl}
              isPdf={isPdf}
            />
          </div>
          {error && <div className="alert">{error}</div>}
          <div className="stack" style={{ marginBottom: "0.85rem" }}>
            <button
              type="button"
              className="btn btn-danger btn-block"
              disabled={busy}
              onClick={unmatch}
            >
              {busy ? "Undoing…" : "Undo match"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="panel stack">
            <div>
              <div className="muted" style={{ fontSize: "0.8rem" }}>
                Merchant
              </div>
              <h2
                style={{
                  fontSize: "1.5rem",
                  width: "100%",
                  overflowWrap: "anywhere",
                  lineHeight: 1.25,
                }}
              >
                {txn.merchant_name}
              </h2>
              {txn.merchant_category && (
                <div className="muted" style={{ marginTop: "0.25rem" }}>
                  {txn.merchant_category}
                </div>
              )}
            </div>

            <div className="row between">
              <div>
                <div className="muted" style={{ fontSize: "0.8rem" }}>
                  Settlement
                </div>
                <div className="amount" style={{ fontSize: "1.6rem" }}>
                  {formatMoney(txn.amount, txn.currency)}
                </div>
                {hasFx && (
                  <div className="muted" style={{ fontSize: "0.85rem" }}>
                    Original {formatMoney(txn.original_amount!, txn.original_currency!)}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="muted" style={{ fontSize: "0.8rem" }}>
                  Transaction date
                </div>
                <div>{formatDate(txn.transaction_date)}</div>
                {txn.booking_date && (
                  <div className="muted" style={{ fontSize: "0.8rem", marginTop: "0.35rem" }}>
                    Booked {formatDate(txn.booking_date)}
                  </div>
                )}
              </div>
            </div>

            {txn.match_notes && (
              <div className="alert alert-ok" style={{ margin: 0 }}>
                {txn.match_notes}
              </div>
            )}
            {error && <div className="alert">{error}</div>}

            {!hasReceipt && txn.status !== "excluded" && (
              <Link
                className="btn btn-accent btn-block"
                to={`/capture?txn=${txn.id}`}
              >
                Add receipt
              </Link>
            )}
            {hasReceipt && txn.matched_receipt_id && !matchedReceipt && (
              <p className="muted" style={{ margin: 0 }}>
                Loading matched receipt…
              </p>
            )}
            {hasReceipt && (
              <button
                type="button"
                className="btn btn-danger btn-block"
                disabled={busy}
                onClick={unmatch}
              >
                {busy ? "Undoing…" : "Undo match"}
              </button>
            )}
            {(txn.status === "unmatched" ||
              txn.status === "suggested" ||
              txn.status === "rejected") &&
              !noReceiptOpen && (
                <button
                  type="button"
                  className="btn btn-ghost btn-block"
                  disabled={busy}
                  onClick={() => {
                    setNoReceiptOpen(true);
                    setError(null);
                  }}
                >
                  No receipt for this
                </button>
              )}
            {noReceiptOpen &&
              (txn.status === "unmatched" ||
                txn.status === "suggested" ||
                txn.status === "rejected") && (
                <div className="stack" style={{ gap: "0.55rem" }}>
                  <div className="field">
                    <label htmlFor="no-receipt-reason">Why is there no receipt?</label>
                    <textarea
                      id="no-receipt-reason"
                      rows={3}
                      value={noReceiptReason}
                      onChange={(e) => setNoReceiptReason(e.target.value)}
                      placeholder="e.g. Lost ticket, private expense, paid in cash…"
                      required
                      minLength={3}
                      style={{
                        width: "100%",
                        borderRadius: 12,
                        border: "1px solid var(--line)",
                        padding: "0.65rem 0.75rem",
                        font: "inherit",
                        resize: "vertical",
                      }}
                    />
                  </div>
                  <div className="row" style={{ gap: "0.5rem" }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={busy || noReceiptReason.trim().length < 3}
                      onClick={markNoReceipt}
                      style={{ flex: 1 }}
                    >
                      {busy ? "Saving…" : "Confirm — no receipt"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost"
                      disabled={busy}
                      onClick={() => {
                        setNoReceiptOpen(false);
                        setNoReceiptReason("");
                        setError(null);
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            {txn.status === "excluded" && (
              <>
                <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
                  Marked as needing no receipt
                  {txn.match_notes ? `: “${txn.match_notes}”` : "."}
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled={busy}
                  onClick={reopen}
                >
                  {busy ? "Reopening…" : "Needs a receipt after all"}
                </button>
              </>
            )}
          </div>

          {!showSplit && (
            <div className="panel stack" style={{ marginTop: "0.85rem" }}>
              <h3 style={{ fontSize: "1rem", margin: 0 }}>All details</h3>
              <dl className="detail-list">
                <DetailRow label="Internal ID" value={txn.id} mono />
                <DetailRow label="External ID" value={txn.external_id} mono />
                <DetailRow label="Status" value={txn.status} />
                <DetailRow label="Merchant" value={txn.merchant_name} />
                <DetailRow label="Category" value={txn.merchant_category} />
                <DetailRow label="Description" value={txn.description} />
                <DetailRow
                  label="Settlement amount"
                  value={formatMoney(txn.amount, txn.currency)}
                />
                <DetailRow label="Settlement currency" value={txn.currency} mono />
                <DetailRow
                  label="Original amount"
                  value={
                    txn.original_amount != null
                      ? formatMoney(
                          txn.original_amount,
                          txn.original_currency || txn.currency,
                        )
                      : null
                  }
                />
                <DetailRow label="Original currency" value={txn.original_currency} mono />
                <DetailRow label="Exchange rate" value={txn.exchange_rate} mono />
                <DetailRow
                  label="Transaction date"
                  value={formatDate(txn.transaction_date)}
                />
                <DetailRow
                  label="Booking date"
                  value={txn.booking_date ? formatDate(txn.booking_date) : null}
                />
                <DetailRow
                  label="Card"
                  value={txn.card_last4 ? `••${txn.card_last4}` : null}
                  mono
                />
                <DetailRow label="Cardholder" value={txn.cardholder_name} />
                <DetailRow label="Cardholder ID" value={txn.cardholder_id} mono />
                <DetailRow
                  label="Match confidence"
                  value={
                    confidence != null && !Number.isNaN(confidence)
                      ? `${confidenceLabel(confidence)} (${(confidence * 100).toFixed(1)}%)`
                      : null
                  }
                />
                <DetailRow label="Match notes" value={txn.match_notes} />
                <DetailRow
                  label="Matched receipt ID"
                  value={txn.matched_receipt_id}
                  mono
                />
                <DetailRow
                  label="Matched at"
                  value={txn.matched_at ? formatDateTime(txn.matched_at) : null}
                />
                <DetailRow label="Matched by user ID" value={txn.matched_by_id} mono />
                <DetailRow label="Import batch ID" value={txn.import_batch_id} mono />
                <DetailRow label="Statement / period" value={txn.import_period_label} />
                <DetailRow label="Import file" value={txn.import_filename} />
                <DetailRow
                  label="Import source"
                  value={
                    txn.import_source === "boekhoudgemak"
                      ? "Boekhoudgemak (bank)"
                      : txn.import_source === "ing_mastercard"
                        ? "ING credit card CSV"
                        : txn.import_source
                  }
                />
                <DetailRow
                  label="Created"
                  value={txn.created_at ? formatDateTime(txn.created_at) : null}
                />
                <DetailRow
                  label="Updated"
                  value={txn.updated_at ? formatDateTime(txn.updated_at) : null}
                />
              </dl>
            </div>
          )}
        </>
      )}

      <InsufficientInfoPanel
        txn={txn}
        isAdmin={isAdmin}
        onChanged={reload}
        style={showSplit ? undefined : { marginTop: "0.85rem" }}
      />

      {isAdmin && (
        <div className="panel stack" style={{ marginTop: "0.85rem" }}>
          <h3 style={{ fontSize: "1rem", margin: 0 }}>Assign to person</h3>
          <p className="muted" style={{ margin: 0, fontSize: "0.85rem" }}>
            {txn.cardholder_name
              ? `Currently with ${txn.cardholder_name}.`
              : "Nobody sees this transaction yet — bank lines from Boekhoudgemak arrive without a cardholder."}
          </p>
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <select
              value={assignTo}
              onChange={(e) => setAssignTo(e.target.value)}
              style={{ flex: 1, minWidth: "12rem" }}
            >
              <option value="">— Unassigned —</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.full_name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={assign}
            >
              {busy ? "Saving…" : "Assign"}
            </button>
          </div>
        </div>
      )}

      <SiblingNav
        currentId={txn.id}
        siblingIds={siblingIds}
        onGo={goSibling}
        busy={busy}
        compact
      />
      <BottomNav />
    </div>
  );
}
